import {Injectable} from '@angular/core';
import {BehaviorSubject} from "rxjs/internal/BehaviorSubject";
import {BuilderStepsEnum, BuilderTourEnum} from "./builder-tour.enum";
import {PageMetaI, SecondaryPageWithSectionsI, StorageSiteStateI} from "./builder-v2.interface";
import * as fromStore from '../store';
import {Store} from "@ngrx/store";
import {DbStoreState} from "../store";
import {filter, first, switchMap} from "rxjs/operators";
import {UserService} from "../user/user.service";
import {Config} from "../config/config";
import {CommonBuilderDataService} from "./services/common-builder-data.service";

@Injectable({
  providedIn: 'root'
})
export class BuilderHistoryService {
  canRedo: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  canUndo: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentStateIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  latestStateIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  canvasScale: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  history: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  tourStep: BehaviorSubject<number> = new BehaviorSubject<number>(BuilderTourEnum.OUTLINE_POPUP);
  newTourStep: BehaviorSubject<number> = new BehaviorSubject<number>(BuilderTourEnum.OUTLINE_POPUP);
  builderStep: BehaviorSubject<number> = new BehaviorSubject<number>(BuilderStepsEnum.CHOOSE_BUSINESS_TYPE);
  private uniqueIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  downgradedToBase: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isTier1Country = false;
  isVibeCoding = false;

  private _history = [];
  private _currentStateIndex = -1;
  private _localStorage = {};
  private storageKey = 'formData';


  constructor(private _store: Store<DbStoreState>,
              private _userService: UserService,
              private _commonDataService: CommonBuilderDataService) {
  }

  addStateInHistory(state) {
    if (this._currentStateIndex < this._history.length - 1) {
      this._history.length = this._currentStateIndex + 1;
    }
    this._history.push(state);
    this._currentStateIndex++;
    this.latestStateIndex.next(this._currentStateIndex);
    this.history.next(this._history);
    this._checkActions();
  }

  addInitialState(sections) {
    this._history?.[0]?.sections?.map((section, i) => {
      const sectionWithType = sections.find(sec => sec.id === section.id);
      if (sectionWithType) {
        section.section_type = sectionWithType.section_type;
      }
    });
  }

  addInitialStateInSecondary(sectionsWithType, pageId: number) {
    this._history.at(0).secondaryPages.map(({page, sections}, i) => {
      if (page.id === pageId) {
        sections.map((section) => {
          const sectionWithType = sectionsWithType.find(sec => sec.id === section.id);
          if (sectionWithType) {
            section.section_type = sectionWithType.section_type;
          }
          return section;
        });
        return sections;
      }
    });
  }

  addSecondaryPageInHistory(secondaryPages: SecondaryPageWithSectionsI[], isFirst: boolean = false) {
    if (isFirst) {
      this._currentStateIndex = -1;
    }
    const history = this.history.value[this.currentStateIndex.value];
    this.addStateInHistory({...history, secondaryPages, id: new Date().getTime()});
  }

  changeCurrentState(direction: number) {
    this._currentStateIndex += direction;
    this.currentStateIndex.next(this._currentStateIndex);
    this._checkActions();
  }

  private _checkActions() {
    this.canRedo.next((this._history.length - 1 > this._currentStateIndex));
    this.canUndo.next((this._currentStateIndex > 0));
  }

  private _setClarityCustomId(uniqueId: string) {
    if (typeof window !== "undefined" && typeof window["clarity"] !== 'undefined') {
      window["clarity"]('identify', uniqueId, uniqueId);
    }
  }

  saveStateInStorage(data: StorageSiteStateI) {
    data.page_outline.sections = data.page_outline.sections.map(({
                                                                   title_control,
                                                                   description_control,
                                                                   ...rest
                                                                 }) => rest);
    data.pages_meta = data.pages_meta?.map((page) => {
      return {
        ...page,
        sections: page?.sections?.map(({
          title_control,
          description_control,
          ...rest
        }) => {
          return rest;
        }) || []
      };
    }) || [];
    localStorage.setItem('builder_history', JSON.stringify(data));
  }

  saveSinglePageInStorage(data: StorageSiteStateI & { page_form: any; single_page: PageMetaI; site_data?: any }) {
    data.single_page.sections = data.single_page.sections.map(({
                                                                 title_control,
                                                                 description_control,
                                                                 ...rest
                                                               }) => rest);
    localStorage.setItem('single_page', JSON.stringify(data));
  }

  getStateFromStorage(): StorageSiteStateI | null {
    return JSON.parse(localStorage.getItem('builder_history'));
  }

  getPageFromStorage(): StorageSiteStateI & { page_form: any; single_page: PageMetaI; site_data: any } | null {
    return JSON.parse(localStorage.getItem('single_page'));
  }

  removePageFromStorage() {
    localStorage.removeItem('single_page');
  }

  removeStateFromStorage() {
    localStorage.removeItem('builder_history');
    sessionStorage.removeItem('websiteType');
    localStorage.removeItem('builderType');
    this.removeHeroSectionFromStorage();
    this.removeHeroSectionFromStorage();
  }

  getLocalStorage() {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserLocalStorage)),
        first()
      )
      .subscribe((userLocalStorage: any) => {
        this._localStorage = userLocalStorage;
      });
  }

  setToUserLocalStorage(isOnboardingFlow: boolean) {
    this._localStorage['isBuilderV2'] = true;
    if (isOnboardingFlow) {
      this._localStorage[`seenNewAiFlow ${Config.demoDomainId}`] = true;
    }
    this._userService.setToLocal(this._localStorage).pipe(
      first(response => !!response, null),
    ).subscribe(response => {
      if (response.success) {
        this._store.dispatch(new fromStore.LoadUserLocalStorage());
      }
    });
  }

  resetSubjects() {
    this.canRedo.next(false);
    this.canUndo.next(false);
    this.currentStateIndex.next(0);
    this.canvasScale.next(1);
    this.history.next([]);
    this.latestStateIndex.next(0);
    this.tourStep.next(BuilderTourEnum.OUTLINE_POPUP);
    this._history = [];
    this._currentStateIndex = -1;
    this._localStorage = {};
  }


  setSessionId(uniqueId: string): void {
    this.uniqueIdSubject.next(uniqueId);
    const existingSessionId = sessionStorage.getItem('sessionId');

    // Always update sessionStorage with the new uniqueId to prevent different traces from sharing the same unique_id
    if (!existingSessionId || existingSessionId !== uniqueId) {
      this._setClarityCustomId(uniqueId);
      sessionStorage.setItem('sessionId', uniqueId);
    }
  }

  getUniqueId(): any {
    let sessionId = sessionStorage.getItem('sessionId');

    return sessionId;
  }

  saveFormData(newData: any): void {
    // Retrieve the existing data from localStorage
    const existingData = this.getFormData() || {};

    // Merge the new data with the existing data
    const updatedData = {...existingData, ...newData};

    // Save the merged data back to localStorage
    localStorage.setItem(this.storageKey, JSON.stringify(updatedData));
  }

  // Retrieve data from localStorage
  getFormData(): any {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : null;
  }

  // Clear data from localStorage
  clearFormData(): void {
    localStorage.removeItem('formData');
  }

  getHeroSectionFromStorage(): any {
    return JSON.parse(sessionStorage.getItem('hero_section'));
  }

  setHeroSectionFromStorage(data) {
    sessionStorage.setItem('hero_section', JSON.stringify(data));
  }

  removeHeroSectionFromStorage() {
    sessionStorage.removeItem('hero_section');
  }

  getHeroSectionRespFromStorage(): any {
    return JSON.parse(sessionStorage.getItem('hero_section_resp'));
  }

  setHeroSectionRespFromStorage(data) {
    sessionStorage.setItem('hero_section_resp', JSON.stringify(data));
  }

  removeHeroSectionRespFromStorage() {
    sessionStorage.removeItem('hero_section_resp');
  }

    setProductId(user?: any, isVibeCoding: boolean = false, changeProductId: boolean = false): void {
        if (user) {
            return;
        }
        const vibeCodingFlow = false;
        const productId =  this._commonDataService.prefilledAi && isVibeCoding ? Config.vibeCodingPluginId : (!changeProductId && localStorage.getItem('product_id') ? localStorage.getItem('product_id') : ((isVibeCoding || vibeCodingFlow ) && this._commonDataService.prefilledAi && !changeProductId ? Config.vibeCodingPluginId :  Config.builder2HostedPluginId));
        localStorage.setItem('product_id', productId.toString());
    }

}
